import { useLogger } from '../../composables/useLogger'
import { useSupabaseClientStore } from '../../stores/supaBaseClientStore'
import { Helper } from './Helper';
import type { ProviderResponse } from '../../composables/useGetProviderService';
import { useGetProviderService } from '../../composables/useGetProviderService';

const logger = useLogger()

interface PersonRow {
    id: string;
    created_at: string | null;
    opt_auth_id: string | null;
    firstname: string | null;
    lastname: string | null;
    email: string | null;
    users?: { id: string }[];
    helpers?: { id: string }[];
}

export class Person {
    id: string;
    created_at: string | null;
    opt_auth_id: string | null;
    firstname: string | null;
    lastname: string | null;
    email: string | null;

    constructor(id: string) {
        this.id = id;
    }

    static getSupabase() {
        const supabaseClientStore = useSupabaseClientStore();
        return supabaseClientStore.supabase;
    }   

    static async fromDatabaseRow(row: PersonRow) : Promise<Person> {
        logger.debug('Creating Person from row:', row);

        const person = new Person(row.id);
        Object.assign(person, {
            created_at: row.created_at,
            opt_auth_id: row.opt_auth_id,
            firstname: row.firstname,
            lastname: row.lastname,
            email: row.email
        });

        logger.debug('Created person object:', person);
        return person;
    }

    static async fromId(id: string) : Promise<Person> {
        const supabase = this.getSupabase();
        const { data, error } = await supabase
            .from('persons')
            .select('*')
            .eq('id', id)
            .single();
        if (error) logger.throwSupabaseError(error, `Failed to get person with ID ${id}`);
        return this.fromDatabaseRow(data);
    }

    static async fromAuthId(authId: string) {
        const supabase = this.getSupabase();
        const { data, error } = await supabase
            .from('persons')
            .select('*')
            .eq('opt_auth_id', authId);
        if (error) logger.throwSupabaseError(error, `Failed to get person with auth ID ${authId}`);
        if (data.length === 0) {            
            return null;
        } else if (data.length === 1) {
            return this.fromDatabaseRow(data[0]);
        } else {
            throw new Error(`Expected exactly one person for authId, authid = ${authId}`)
        }
    }

    /* retrieve zero or one person from the database by email */
    static async fromEmail(email: string) : Promise<Person | null> {
        const supabase = this.getSupabase();
        const { data, error } = await supabase
            .from('persons')
            .select('*')
            .eq('email', email)
        if (error) logger.throwSupabaseError(error, `Failed to get person with email ${email}`);
        if (data.length === 1) {
            return this.fromDatabaseRow(data[0]);
        } else if (data.length === 0) {
            return null;
        } else {
            throw new Error('Expected exactly one person for email')
        }
    }

    /**
     * Get the provider and person for a person with this email. 
     * Null if no person with this email.
     * Throw if not exactly one person and exactly one provider.
     * @param email 
     * @returns { person: Person, provider: string} | null
     */
    static async getPersonAndProviderByEmail(email: string): Promise<{ person: Person, provider: string} | null> {
        const person = await Person.fromEmail(email)
        let provider = null
      
        if (!person) {
            return null
        }
      
        const opt_auth_id = person.opt_auth_id
        if (!opt_auth_id) {
          throw new Error('Person with email ' + email + ' has no auth_id')
        }
        try {
          const { getProvider } = useGetProviderService()
          const providerResponse: ProviderResponse = await getProvider({ auth_id: opt_auth_id })
          return { person: person, provider: providerResponse.provider }
        } catch (error) {
          logger.logErrorCaught('Error getting provider for person with email ' + email, 'Person.ts', error)
          throw new Error(
            'Error getting provider for person with email ' + email,
            { cause: error }
          )
        }
      }
      

    static async existsByAuthId(authId: string) {
        const person = await this.fromAuthId(authId)
        return person !== null
    }

    static async createNewPerson(authId: string | null, firstname: string, lastname: string, email: string | null = null) {
        const supabase = this.getSupabase();
        const { data, error } = await supabase
            .from('persons')
            .insert({
                opt_auth_id: authId,
                firstname: firstname,
                lastname: lastname,
                email: email
            })
            .select()
            .single();
        if (error) logger.throwSupabaseError(error, `Failed to create new person with firstname ${firstname}`);
        return this.fromDatabaseRow(data);
    }

    static async delete(id: string) {
        const supabase = this.getSupabase();
        const { error } = await supabase.from('persons').delete().eq('id', id);
        if (error) logger.throwSupabaseError(error, `Failed to delete person with ID ${id}`);
    }

    static async getAllPersonsThatCanBeHelpers(): Promise<Person[]> {
        const supabase = this.getSupabase();
        const { data, error } = await supabase
            .from('persons')
            .select(`
                id,
                created_at,
                opt_auth_id,
                firstname,
                lastname,
                email
            `)
            .not('opt_auth_id', 'is', null)
            .order('firstname');
        
        if (error) {
            logger.error('Database error:', error);
            logger.throwSupabaseError(error, 'Failed to get all persons that can be helpers');
        }
        
        logger.debug('Raw persons data:', data);
        
        return data.map(row => {
            const person = new Person(row.id);
            person.created_at = row.created_at;
            person.opt_auth_id = row.opt_auth_id;
            person.firstname = row.firstname;
            person.lastname = row.lastname;
            person.email = row.email;
            logger.debug('Created person:', person);
            return person;
        });
    }

    async getAllHelperRolesPersonHas(): Promise<Helper[]> {
        const supabase = Person.getSupabase();
        const { data, error } = await supabase
            .from('helpers')
            .select('*')
            .eq('person_id', this.id);
        if (error) logger.throwSupabaseError(error, `Failed to get helper roles for person ${this.id}`);
        const helpers = await Helper.fromDatabaseRows(data);
        return helpers;
    }

}